/**
 * MARKETING — les campagnes d'un magasin : les écrire, compter qui les recevra, les envoyer.
 *
 * ⚠️ PORTÉ SUR LA MAQUETTE (`design/Sliderz Backoffice.dc.html`, bloc `mk*`) : cinq repères
 * de tête, une carte à barre d'outils (recherche + pastilles comptées), et des LIGNES riches — pas
 * un tableau générique. Géométries, icônes et jetons de couleur sont relevés verbatim ; ce qui
 * change, ce sont les nombres, qui viennent tous du serveur.
 *
 * ⚠️ CET ÉCRAN NE MONTRE QUE DU RÉEL. Les compteurs viennent de la base, le compte rendu vient des
 * LIGNES PAR DESTINATAIRE (pas des totaux de la campagne), et un envoi affiche ce qui est réellement
 * parti. Aucun état n'est anticipé : on relit après l'appel (§23.0).
 *
 * ⚠️ TROIS DES CINQ REPÈRES NE SONT PAS CALCULABLES AUJOURD'HUI — automatismes (pas d'ordonnanceur),
 * taux d'ouverture (pas d'accusés de réception) et CA rattaché (pas d'attribution). Ils sont DESSINÉS
 * et marqués « à venir » plutôt qu'absents : les retirer laisserait croire qu'on les a oubliés, et
 * les remplir d'un nombre serait de la donnée inventée. Un « — » muet aurait le même défaut — il se
 * lirait « zéro ouverture », donc chaque tuile dit POURQUOI elle est vide.
 *
 * ⚠️ LE BOUTON D'ENVOI N'EXISTE QUE SI LE SERVEUR DIT QU'IL PEUT ENVOYER (`canSend`). Sans transport
 * câblé, la route `/send` n'est pas montée : afficher le bouton actif offrirait une action qui
 * rendrait 404 au premier clic. Il reste VISIBLE et désactivé, avec ce qui manque.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Badge, Button, confirm, EmptyState, KpiRow, Modal, showToast, Trans, useLatest, type KpiItem } from '@orgazone/ui-web';
// ⚠️ Le TON vient des JETONS, pas du composant : c'est la charte qui possède le vocabulaire.
import type { BadgeTone } from '@orgazone/ui-tokens';
import { formatMoney } from '@orgazone/money-format';
import { api, type Campaign, type CampaignActivity, type CampaignAttribution, type CreditVerdict , type CampaignTemplate } from '../api';
import { useStoreScope } from '../store-context';
// ⚠️ LE WIZARD VIT DANS SON PROPRE MODULE : c'est la seule pièce transposée mesure par mesure du DOM
//    de la maquette, et la noyer dans la liste rendait ses relevés impossibles à relire.
import { SANS_MOTEUR, WizardCampagne } from './marketing-wizard';
import { key, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { channelLabel, channelMetric, channelReachability } from '../campaign-channels';

/** Les tracés SVG de la maquette — relevés verbatim, jamais redessinés à l'estime. */
const IC = {
  /** Éclair — les automatismes. */
  auto: 'M13 2L3 14h7l-1 8 10-12h-7l1-8z',
  /** Avion en papier — une campagne ponctuelle, et les envois. */
  ponct: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  /** Enveloppe — le taux d'ouverture. */
  ouverture: 'M4.5 5h15a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM3 7l9 7 9-7',
  /** Euro — le chiffre d'affaires rattaché. */
  euro: 'M18.5 5.5A7.5 7.5 0 0 0 13 3.5a8 8 0 0 0 0 17 7.5 7.5 0 0 0 5.5-2M3.5 10h10M3.5 14h8.5',
  /** Horloge — la réserve d'envoi qui s'épuise. */
  credits: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7.5V12l3 2',
  plus: 'M12 5v14M5 12h14',
  /** Triangle d'avertissement — repris VERBATIM de l'assistant : deux dessins du même signe dans le
   *  même module se verraient côte à côte. */
  alerte: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01',
} as const;

/** Une icône de la maquette, à la géométrie de trait de la charte. */
function Trace({ d, size = 15 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

/**
 * Les états d'une campagne — libellé et TON de pastille.
 * ⚠️ UN TON, PAS UNE COULEUR. Une première version peignait chaque pastille à la main
 * (`bg: 'var(--rose-bg)'`) d'après le relevé de la maquette : c'était une troisième table de
 * couleurs dans un dépôt qui en a déjà une (`BADGE_TONES`), et elle aurait dérivé au premier
 * ajustement de charte. Le rôle est nommé, la teinte appartient au kit.
 */
const ETATS: Record<string, { label: Translatable; tone: BadgeTone }> = {
  draft: { label: key('Brouillon'), tone: 'neutral' },
  ready: { label: key('Prête'), tone: 'info' },
  sending: { label: key('Envoi en cours'), tone: 'info' },
  paused: { label: key('Suspendue'), tone: 'warn' },
  done: { label: key('Terminée'), tone: 'ok' },
  failed: { label: key('En échec'), tone: 'danger' },
};

/** L'état d'une campagne, prêt à afficher — appelé au rendu, il résout la clé de l'état connu ; un état inconnu se montre tel quel. */
const etatDe = (s: string): { label: string; tone: BadgeTone } => {
  const e = ETATS[s];
  return e ? { label: t(e.label), tone: e.tone } : { label: s, tone: 'neutral' };
};


const nombre = (n: number): string => n.toLocaleString('fr-FR');
/** Un montant en centimes, rendu par le formateur de la plateforme — jamais un arrondi maison. */
const euros = (cents: number): string => formatMoney(cents, { thousandsSep: ' ' });

const dateCourte = (ms: number | null): string =>
  (ms ? new Date(ms).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');

/**
 * Le bloc d'erreur — EN LIGNE, avec les jetons du thème.
 * ⚠️ Le back-office n'embarque AUCUNE classe utilitaire : une première version portait
 * `className="err"`, recopiée de la Console où cette classe existe. Attrapé par
 * `audit-classes-css` — pas par `tsc`, qui ne connaît pas le vocabulaire d'une SPA.
 */
const blocErreur = {
  border: '1px solid var(--danger-ink)', borderRadius: 'var(--r-sm)', padding: '8px 10px',
  color: 'var(--danger-ink)', fontSize: 'var(--t-note)', lineHeight: 1.6,
} as const;

/** Le message d'une exception, tel quel — jamais réécrit en phrase rassurante. */
const motif = (e: unknown): string => (e as Error)?.message || t('Motif inconnu.');

/** Les jours de semaine, dans l’ordre du modèle (1 = lundi). */
const JOURS_FR = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const;

/**
 * LA CADENCE D’UN MODÈLE, EN UNE PHRASE — c’est elle qui dit à l’exploitant ce qu’il a mis en service.
 * ⚠️ ON RÉÉCRIT L’HEURE À DEUX CHIFFRES plutôt que de la rendre nue : « part à 9:0 » se lit comme un
 * bogue, et fait douter du reste de la ligne.
 */
export function cadenceLisible(r: CampaignTemplate['recurrence']): string {
  const h = `${String(r.hour).padStart(2, '0')}:${String(r.minute).padStart(2, '0')}`;
  if (r.kind === 'once') return t('Une seule fois, le {v} à {h}', { v: r.date ?? '—', h });
  if (r.kind === 'weekly') {
    const jours = r.days.map((d) => JOURS_FR[d - 1] ?? '?').join(', ');
    return t('Chaque semaine — {jours} à {h}', { jours, h });
  }
  return t('Chaque mois — le {v} à {h}', { v: r.days.join(', '), h });
}

/**
 * LA PROCHAINE ÉCHÉANCE, telle qu'un humain la lit.
 * ⚠️ 🔴 `null` NE SE DIT PAS « — » : un modèle en service sans prochaine date ne partira JAMAIS, et
 * c’est précisément ce qu’il faut voir. Un tiret laisserait croire à une information manquante.
 */
export function echeanceLisible(at: number | null): string {
  if (at === null) return t('plus rien de prévu');
  return t('part le {date}', { date: new Date(at).toLocaleString(currentLang(), { dateStyle: 'medium', timeStyle: 'short' }) });
}

type Credits = Awaited<ReturnType<typeof api.campaignCredits>>['credits'];

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LES CINQ REPÈRES DE TÊTE (maquette `mkKpis`)
// ══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * La réserve d'envoi, en une ligne de tuile.
 * ⚠️ LES DEUX CANAUX DANS LE MÊME REPÈRE, ET ILS NE SE COMPTENT PAS PAREIL : le SMS a une réserve
 * décomptable, l'e-mail est facturé au plan. Afficher « 0 e-mails » ferait croire à un blocage.
 */
function texteCredits(c: Credits | null): { value: string | null; sub: string; low: boolean } {
  if (!c) return { value: null, sub: t('Solde non lu — la réserve de l’opérateur n’a pas encore répondu.'), low: false };
  if (c.unavailable) return { value: null, sub: c.unavailable, low: false };
  const sms = c.available === null ? null : Math.floor(Number.parseFloat(c.available));
  if (sms === null || !Number.isFinite(sms)) {
    return { value: null, sub: t('Le compte ne rend pas de solde lisible.'), low: false };
  }
  // ⚠️ LA VALEUR RESTE COURTE, L'EXPLICATION VA AU SURVOL. La maquette annonce deux nombres
  //    (« … SMS · … emails ») ; nous n'en avons qu'un, et écrire la raison dans la tuile la faisait
  //    déborder en « 780 SMS · e-mail au … ». Un chiffre coupé par des points de suspension est pire
  //    qu'un chiffre seul : on ne sait plus s'il manque une unité ou un ordre de grandeur.
  return {
    value: t('{v} SMS', { v: nombre(sms) }),
    // ⚠️ DEUX PHRASES ENTIÈRES plutôt qu'un morceau optionnel glissé en valeur : chaque langue place la
    //    mention de la plateforme où sa grammaire le veut.
    sub: c.source === 'platform'
      ? t('Réserve du compte {v} (plateforme) — l’e-mail n’apparaît pas ici : Mailjet facture au plan, il n’a pas de réserve décomptable.', { v: c.account ?? '—' })
      : t('Réserve du compte {v} — l’e-mail n’apparaît pas ici : Mailjet facture au plan, il n’a pas de réserve décomptable.', { v: c.account ?? '—' }),
    low: sms < 50,
  };
}

/**
 * Les cinq repères — `KpiRow` du kit, comme les six autres écrans de liste.
 * ⚠️ `value: null` = « à venir », rendu par la primitive : trois de ces cinq mesures n'existent pas
 * encore, et c'est la primitive qui sait comment le dire, pas cet écran.
 */
/**
 * LE CHIFFRE D'AFFAIRES DES DESTINATAIRES, tous envois confondus — et `null` quand rien n'a servi.
 *
 * ⚠️ 🔴 CE N'EST PAS « LE CA RATTACHÉ », et le repère change donc de nom. « Rattaché » laisse entendre
 * qu'on sait relier une vente à une campagne : on ne sait pas. On sait que ces clients-là ont acheté
 * après avoir été servis, ce qui est vrai et beaucoup plus faible.
 * ⚠️ ON SOMME LES CAMPAGNES, PAS LES CLIENTS : un même client servi par deux campagnes compte dans les
 * deux, et son achat aussi. Le total répond à « combien mes envois ont-ils accompagné de chiffre
 * d'affaires », jamais à « combien ai-je encaissé grâce au marketing » — la seconde question demande
 * un témoin, et le sous-titre le dit.
 * ⚠️ `null` QUAND AUCUNE CAMPAGNE N'A SERVI PERSONNE : « 0,00 € » se lirait « mes envois ne
 * rapportent rien », alors que rien n'est encore parti.
 */
export function caDesDestinataires(
  attribution: readonly CampaignAttribution[],
): { ttcCents: number; windowDays: number } | null {
  const servies = attribution.filter((a) => a.served > 0);
  if (servies.length === 0) return null;
  return {
    ttcCents: servies.reduce((n, a) => n + a.ttcCents, 0),
    // La fenêtre vient du serveur ; toutes les campagnes partagent la même, on prend celle qui arrive.
    windowDays: servies[0]!.windowDays,
  };
}

function Reperes({ activity, attribution, credits }: {
  activity: CampaignActivity | null;
  attribution: readonly CampaignAttribution[];
  credits: Credits | null;
}) {
  const ca = caDesDestinataires(attribution);
  const cred = texteCredits(credits);
  const kpis: KpiItem[] = [
    // ⚠️ « À VENIR » PLUTÔT QUE ZÉRO : aucun ordonnanceur n'existe, donc « 0 automatisme actif » serait
    //    vrai par accident et faux par intention — il se lirait « je n'en ai pas encore créé ».
    {
      label: t('Automatismes actifs'), value: null, icon: IC.auto, tone: 'teal',
      sub: t('À venir — anniversaire, inactivité, cagnotte à expirer : les déclencheurs demandent un ordonnanceur.'),
    },
    {
      label: t('Envoyés — {jours}', { jours: t('{n} jour', { n: activity?.windowDays ?? 30 }) }),
      value: activity ? nombre(activity.sentTotal) : null, icon: IC.ponct, tone: 'blue',
      sub: t('Messages réellement partis, comptés une ligne de destinataire à la fois. Les écarts et les échecs n’y sont pas.'),
    },
    {
      label: t('Taux d’ouverture'), value: null, icon: IC.ouverture, tone: 'violet',
      sub: t('À venir — les ouvertures arrivent par les accusés de réception du fournisseur, dont la surface publique n’est pas posée.'),
    },
    {
      // ⚠️ LE TITRE DIT CE QUE C'EST : le CA DES DESTINATAIRES, pas le CA « rattaché ». Le second
      //    laisserait croire qu'on sait relier une vente à une campagne — on ne sait pas.
      label: ca ? t('CA des destinataires — {windowDays} j', { windowDays: ca.windowDays }) : t('CA des destinataires'),
      value: ca ? euros(ca.ttcCents) : null, icon: IC.euro, tone: 'blue',
      sub: ca
        ? t('Ce que les destinataires servis ont acheté dans la fenêtre qui suit chaque envoi. ⚠️ Une partie serait arrivée sans campagne : sans groupe témoin, ce chiffre ne dit pas ce que le marketing a CAUSÉ.')
        : t('Aucune campagne n’a encore servi de destinataire — il n’y a rien à mesurer, et « 0,00 € » se lirait « mes envois ne rapportent rien ».'),
    },
    {
      label: t('Crédits d’envoi'), value: cred.value, sub: cred.sub, icon: IC.credits,
      // ⚠️ L'AMBRE EST UN SIGNAL, PAS UNE DÉCORATION (maquette : sous 50 SMS) — c'est le seul repère
      //    dont la valeur basse annonce un envoi qui va s'interrompre.
      tone: cred.low ? 'amber' : 'ink',
    },
  ];
  return <KpiRow items={kpis} />;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LA CARTE : BARRE D'OUTILS + LIGNES (maquette `mkChips` / `mkRows` / `mkCountL`)
// ══════════════════════════════════════════════════════════════════════════════════════════════

type Pastille = 'all' | 'auto' | 'ponct' | 'draft';

/** Le chiffre d'une colonne de mesure d'une ligne de campagne, et la légende posée dessous. */
const STAT_FIGURE: CSSProperties = { display: 'block', fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' };
const STAT_CAPTION: CSSProperties = { display: 'block', fontSize: '10px', color: 'var(--ink-400)' };

/** Une ligne de campagne — icône, identité, ce qu'elle a produit, son état. */
function Ligne({ c, onOuvrir }: { c: Campaign; onOuvrir: () => void }) {
  const etat = etatDe(c.status);
  const audience = c.recipientCount ? t('{n} destinataire', { n: c.recipientCount }) : t('audience non figée');
  const quand = c.status === 'done' && c.finishedAt ? t('Envoyée le {v}', { v: dateCourte(c.finishedAt) })
    : c.status === 'sending' ? t('Envoi en cours')
      : c.status === 'draft' ? t('Brouillon')
        : c.status === 'ready' ? t('Prête depuis le {v}', { v: dateCourte(c.createdAt) })
          : t('Interrompue au {v}ᵉ', { v: nombre(c.processed) });
  // ⚠️ LES ÉCHECS SE DISENT DANS LA LIGNE. Repliés dans le détail, ils obligeraient à ouvrir chaque
  //    campagne pour savoir laquelle a mal fini — exactement ce qu'une liste doit éviter.
  //    Trois étiquettes indépendantes, le séparateur dans le CODE — la clé ne porte jamais « · ».
  const sub = [quand, audience, ...(c.failedCount ? [t('{n} en échec', { n: c.failedCount })] : [])].join(' · ');

  return (
    <div role="button" tabIndex={0} onClick={onOuvrir}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(); } }}
      style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 16px', borderBottom: '1px solid var(--ink-100)', cursor: 'pointer' }}>
      <span style={{ width: 38, height: 38, borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Trace d={IC.ponct} size={18} />
      </span>

      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink-900)' }}>{c.name}</span>
          {/* ⚠️ `Badge` DU KIT, cran `xs`. La maquette dessinait ses propres capsules (18 px, 9,5 px) ;
              le cran `xs` de la charte en fait 19 et 10 — un demi-pixel d'écart ne justifie pas une
              troisième définition de capsule dans le dépôt.
              ⚠️ « PONCTUELLE » EST LA SEULE NATURE POSSIBLE aujourd'hui, et l'étiquette le dit quand
              même : c'est elle qui donnera son sens à « AUTOMATIQUE » le jour des déclencheurs. */}
          <Badge size="xs">{t('PONCTUELLE')}</Badge>
          <Badge size="xs">{channelLabel(c.channel)}</Badge>
        </span>
        <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>
      </span>

      <span style={{ textAlign: 'right', flexShrink: 0, display: 'flex', gap: 18, alignItems: 'center' }}>
        {/* ⚠️ LE NOMBRE ET SA LÉGENDE SONT UNE SEULE PHRASE COMPTÉE : rendue en deux éléments séparés, la légende
            disait « 1 partis ». Le tiret, lui, n'est pas un compte — la légende y reste l'intitulé de la colonne. */}
        <span style={{ minWidth: 74 }}>
          {c.sentCount
            ? <Trans k='<1>{n}</1> <2>parti</2>' vars={{ n: c.sentCount }} parts={[
                (x) => <span style={STAT_FIGURE}>{x}</span>,
                (x) => <span style={STAT_CAPTION}>{x}</span>,
              ]} />
            : <><span style={STAT_FIGURE}>—</span><span style={STAT_CAPTION}>{t('partis')}</span></>}
        </span>
        {/* ⚠️ DEUX COLONNES DÉLIBÉRÉMENT VIDES, ET LEUR MOTIF EST AU SURVOL. Les supprimer ferait
            disparaître de l'écran deux mesures que tout le monde attend d'une campagne — et personne
            ne saurait qu'elles sont en chantier plutôt qu'oubliées. */}
        <span style={{ minWidth: 64 }} title={t('À venir — les ouvertures arrivent par les accusés de réception du fournisseur.')}>
          <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-300)', fontVariantNumeric: 'tabular-nums' }}>—</span>
          <span style={{ display: 'block', fontSize: '10px', color: 'var(--ink-400)' }}>{channelMetric(c.channel)}</span>
        </span>
        <span style={{ minWidth: 78 }} title={t('À venir — rattacher une vente à une campagne demande un porteur d’attribution.')}>
          <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-300)', fontVariantNumeric: 'tabular-nums' }}>—</span>
          <span style={{ display: 'block', fontSize: '10px', color: 'var(--ink-400)' }}>{t('CA rattaché')}</span>
        </span>
        <span style={{ minWidth: 96, display: 'flex', justifyContent: 'flex-end' }}>
          <Badge tone={etat.tone}>{etat.label}</Badge>
        </span>
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE SOLDE, LÀ OÙ IL DÉCIDE
// ══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * LE SOLDE ET SON VERDICT — écrits UNE fois, affichés au même endroit que le nombre de destinataires.
 *
 * ⚠️ LES DEUX CHIFFRES ENSEMBLE, TOUJOURS (cadrage §11.6). Un solde seul ne dit rien ; un effectif
 * seul non plus. C'est leur écart qui décide, et c'est à l'exploitant de trancher — jamais à nous
 * de tronquer l'audience en silence.
 *
 * ⚠️ « INCONNU » SE DIT COMME TEL, avec son motif. Ni vert rassurant, ni rouge alarmant : on n'a
 * pas la donnée, et l'écran n'a pas à en inventer la couleur.
 */
function Solde({ credits, verdict, destinataires, suspendu }: {
  credits: Credits | null;
  verdict: CreditVerdict | null;
  destinataires: number | null;
  /** Motif d'une SUSPENSION de ce canal par Orgazone — `null` quand le magasin a le droit d'envoyer. */
  suspendu?: string | null;
}) {
  // ⚠️ 🔴 LA SUSPENSION PASSE AVANT LE SOLDE, ET AVANT LE `if (!credits)` : un canal suspendu ne
  //    partira pas, quel que soit le nombre de crédits — afficher « 2 951 crédits » à quelqu'un qui
  //    n'a pas le droit d'envoyer serait exactement l'information qui fait cliquer pour rien (§23.0).
  // ⚠️ ET ELLE NE SE CONFOND PAS AVEC « pas de réserve » : les deux ne se réparent pas au même
  //    endroit — l'une s'achète, l'autre se lève auprès d'Orgazone.
  if (suspendu) {
    return (
      <div style={{ marginTop: 8, fontSize: 'var(--t-note)', color: 'var(--danger-ink)', lineHeight: 1.5 }}>
        <strong>{t('Ce canal est suspendu pour ce magasin.')}</strong> {suspendu}
      </div>
    );
  }
  if (!credits) return null;

  if (credits.unavailable) {
    return (
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 8 }}>
        {t('Solde indisponible — {unavailable}', { unavailable: credits.unavailable })}
      </div>
    );
  }

  // ⚠️ LE CHIFFRE DOIT EXISTER POUR ÊTRE DIT : `missing` est `number | null`, et un « short » sans
  //    chiffre afficherait « il en manque 0 » — un manque nul, c'est-à-dire le contraire de ce que le
  //    verdict annonce (§23.0). Sans chiffre, seul le solde s'affiche.
  const court = verdict?.verdict === 'short' && verdict.missing !== null;
  // ⚠️ DEUX SOURCES POUR UN MÊME SOLDE : le verdict le rend en NOMBRE, le compte en CHAÎNE (cinq
  //    décimales chez l'opérateur). On prend le nombre quand il existe, sinon on convertit — et si
  //    rien ne se lit, `null`, qui ne vaut pas zéro.
  const soldeLisible = verdict?.availableMessages
    ?? (credits.available !== null && Number.isFinite(Number(credits.available)) ? Number(credits.available) : null);
  /** Le solde en gras chiffré, le compte en chasse fixe — les deux marqueurs des quatre phrases ci-dessous. */
  const pieces = [
    (x: string) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</strong>,
    (x: string) => <span className="mono">{x}</span>,
  ];
  return (
    <div style={{ marginTop: 8, fontSize: 'var(--t-note)', color: court ? 'var(--danger-ink)' : 'var(--ink-600)' }}>
      {/* ⚠️ LE NOMBRE EST DANS LA PHRASE, pas à côté : « crédit » s’accorde avec lui, et le moteur ne
          peut choisir la forme que s’il le reçoit. Le `<strong>` reste, porté par le marqueur.
          ⚠️ `available` est une CHAÎNE et peut être `null` — « non mesuré, JAMAIS zéro », dit son
          type. Sans nombre lisible, on affiche le solde tel quel et on ne prétend rien accorder. */}
      {/* ⚠️ QUATRE PHRASES ENTIÈRES, le compte DANS la phrase : la mention « (plateforme) » était collée à part,
          un morceau sans verbe que le traducteur recevait seul. */}
      {soldeLisible === null
        ? (credits.source === 'platform'
          ? <Trans k='Solde <1>{v}</1> sur le compte <2>{account}</2> (plateforme)' vars={{ v: credits.available ?? '—', account: credits.account ?? '—' }} parts={pieces} />
          : <Trans k='Solde <1>{v}</1> sur le compte <2>{account}</2>' vars={{ v: credits.available ?? '—', account: credits.account ?? '—' }} parts={pieces} />)
        : (credits.source === 'platform'
          ? <Trans k='<1>{n}</1> crédit sur le compte <2>{account}</2> (plateforme)' vars={{ n: soldeLisible, account: credits.account ?? '—' }} parts={pieces} />
          : <Trans k='<1>{n}</1> crédit sur le compte <2>{account}</2>' vars={{ n: soldeLisible, account: credits.account ?? '—' }} parts={pieces} />)}
      {/* ⚠️ LE MANQUE SE CHIFFRE. « Insuffisant » n'aide pas à décider ; « il en manque 460 » permet
          de réduire l'audience ou de recharger en connaissance de cause. */}
      {court && destinataires !== null && (
        <> <Trans k='— <1>il en manque {missing}</1> pour {v}. L’envoi partira quand même et s’arrêtera à court de crédits : la campagne restera reprenable.' vars={{ missing: verdict!.missing!, v: t('{n} destinataire', { n: destinataires ?? 0 }) }} parts={[(x) => <strong>{x}</strong>]} /></>
      )}
      {verdict?.verdict === 'unknown' && destinataires !== null && (
        <> {t('— impossible de dire s’il couvre les {v}.', { v: t('{n} destinataire', { n: destinataires ?? 0 }) })}</>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE DÉTAIL D'UNE CAMPAGNE — l'audience figée, le solde de crédits, l'envoi
// ══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * CE QU'IL RESTE À ENVOYER — et donc ce que l'envoi va réellement coûter en crédits.
 *
 * ⚠️ 🔴 UN TÉMOIN NE CONSOMME AUCUN CRÉDIT, parce qu'aucun message ne part. Compter « audience moins
 * traités » y inclut les témoins pas encore enjambés, et l'écran annoncerait alors un manque de
 * crédits pour des envois qui n'auront jamais lieu. Sur une campagne à 20 % de témoin, le verdict
 * serait faux d'un cinquième — assez pour faire recharger un compte sans raison.
 *
 * ⚠️ L'ÉTAT `pending` EST EXACTEMENT LA BONNE RÉPONSE : ne sont `pending` que les destinataires ni
 * traités ni témoins — les témoins portent leur état dès la construction de l'audience. Le calcul par
 * soustraction, lui, ne peut pas les distinguer.
 *
 * ⚠️ LE REPLI PAR SOUSTRACTION RESTE, pour le cas où le compte rendu n'est pas encore lu : une tuile
 * vide vaut mieux qu'un zéro, qui se lirait « plus rien à envoyer ».
 */
export function resteAEnvoyer(
  record: { recipientCount: number; processed: number },
  tally: Record<string, number>,
): number {
  const attente = tally.pending;
  if (typeof attente === 'number') return attente;
  return Math.max(0, record.recipientCount - record.processed);
}

function Detail({ id, onChange, onClose }: { id: string; onChange: () => void; onClose: () => void }) {
  const [record, setRecord] = useState<Campaign | null>(null);
  const [tally, setTally] = useState<Record<string, number>>({});
  const [canSend, setCanSend] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [verdict, setVerdict] = useState<CreditVerdict | null>(null);
  /** Le motif d une SUSPENSION de ce canal par Orgazone — distinct du blocage total du magasin. */
  const [canalSuspendu, setCanalSuspendu] = useState<string | null>(null);

  // ⚠️ SEULE LA DERNIÈRE RELECTURE ÉCRIT LE PANNEAU, à ses DEUX attentes : la campagne puis ses crédits. Le verdict des
  //    crédits décide si « Lancer » est offert — celui d'une autre campagne, ou d'avant l'envoi, y mentirait.
  const latest = useLatest();
  const relire = useCallback(async () => {
    const isLatest = latest();
    try {
      const r = await api.campaign(id);
      if (!isLatest()) return;
      setRecord(r.record); setTally(r.tally); setCanSend(r.canSend); setErr(null);

      // ⚠️ LE SOLDE SE RELIT ICI — 2ᵉ ET 3ᵉ DES TROIS MOMENTS (cadrage §11.6) : cette relecture sert
      //    l'ouverture du panneau (AVANT le lancement) ET le retour d'envoi (le solde a bougé). Le
      //    serveur oublie son miroir après un envoi, donc ce second appel rend bien le nouveau chiffre.
      // ⚠️ L'EFFECTIF COMPARÉ EST CE QUI RESTE, pas l'audience entière : sur une reprise, les
      //    12 000 déjà partis sont payés. Comparer au total ferait crier au manque sans raison.
      if (r.canSend) {
        const c = await api.campaignCredits(r.record.channel, resteAEnvoyer(r.record, r.tally));
        if (!isLatest()) return;
        setCredits(c.credits); setVerdict(c.verdict ?? null);
        setCanalSuspendu(c.channelBlocked ?? null);
      }
    } catch (e) { if (isLatest()) setErr(motif(e)); }
  }, [id, latest]);

  useEffect(() => { void relire(); }, [relire]);

  const agir = async (quoi: 'freeze' | 'send') => {
    setBusy(true);
    try {
      if (quoi === 'freeze') {
        const { record: r } = await api.freezeCampaign(id);
        showToast(t('Audience figée : {v}.', { v: t('{n} destinataire', { n: r.recipientCount }) }), 'ok');
      } else {
        const r = await api.sendCampaign(id);
        // ⚠️ ON DIT LE COMPTE DÉBITÉ. « Envoyé » sans dire QUI a payé envoie chercher sa consommation
        //    dans le mauvais compte — et un magasin peut avoir le sien ou partager celui de la plateforme.
        //    Deux phrases ENTIÈRES : la mention « (plateforme) » et le point final étaient collés hors de la clé.
        const bilan = { partis: t('{n} parti', { n: r.report.sent }), failed: r.report.failed, ecartes: t('{n} écarté', { n: r.report.skipped }), account: r.account };
        showToast(
          r.source === 'platform'
            ? t('{partis}, {failed} en échec, {ecartes} — compte {account} (plateforme).', bilan)
            : t('{partis}, {failed} en échec, {ecartes} — compte {account}.', bilan),
          r.report.failed ? 'err' : 'ok',
        );
      }
      await relire();
      onChange();
    } catch (e) {
      showToast(motif(e), 'err');
      // ⚠️ ON RELIT MÊME APRÈS UN ÉCHEC : le serveur a pu consigner l'état `failed` avec son motif,
      //    et l'écran doit montrer ce que la base dit, pas ce que le clic espérait.
      await relire();
      onChange();
    } finally { setBusy(false); }
  };

  const etat = record ? etatDe(record.status) : null;
  const envoyable = record ? ['ready', 'paused', 'failed'].includes(record.status) : false;
  const reste = record ? resteAEnvoyer(record, tally) : 0;
  const temoins = tally.holdout ?? 0;

  return (
    <Modal
      open onClose={onClose} width="lg" icon="mail" tone="blue"
      title={record?.name ?? t('Campagne')}
      subtitle={record ? `${channelLabel(record.channel)} · ${etat!.label}` : t('Lecture…')}
      blockClose={() => busy}
      footer={record ? (
        /* ⚠️ LE PIED NE PORTE QUE LES DEUX GESTES QUI ÉCRIVENT. Une première version y alignait
           aussi les trois actions « à venir » : à cinq boutons, le pied débordait et « Dupliquer »
           sortait de la fenêtre — une action rendue INVISIBLE par la mise en page, exactement ce que
           la règle du 2026-09-02 veut éviter. Elles vivent donc dans le corps, où elles ont la place. */
        <>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" disabled={busy || record.status !== 'draft'} onClick={() => void agir('freeze')}
            reason={record.status !== 'draft'
              ? t('L’audience est déjà figée : la reconstruire décalerait les rangs sur lesquels repose la reprise.')
              : undefined}>
            
            {t('Figer l’audience')}
          </Button>
          <Button variant="primary" disabled={busy || !envoyable || !canSend} onClick={() => void agir('send')}
            reason={!canSend
              ? t('Aucun compte de messagerie n’est rattaché à ce back-office : l’envoi n’est pas câblé.')
              : !envoyable
                ? (record.status === 'sending'
                  ? t('Un envoi est en cours — deux passages simultanés serviraient deux fois les mêmes destinataires.')
                  : record.status === 'draft'
                    ? t('Figez d’abord l’audience.')
                    : t('Cette campagne est terminée : elle ne se renvoie pas.'))
                : undefined}>
            {record.processed > 0 ? t('Reprendre au {v}ᵉ', { v: nombre(record.processed + 1) }) : t('Lancer l’envoi')}
          </Button>
        </>
      ) : undefined}
    >
      {err && <div role="alert" style={blocErreur}>{t('Campagne illisible : {err}', { err })}</div>}
      {!record && !err && <div style={{ color: 'var(--ink-500)' }}>{t('Lecture…')}</div>}

      {record && (
        <>
          {/* ⚠️ LE MOTIF RÉEL D'UN ÉCHEC, EN CLAIR. « En échec » sans motif obligerait à deviner entre
              un compte non rattaché, un fournisseur injoignable et un message vide. */}
          {record.lastError && (
            <div role="alert" style={{ ...blocErreur, marginBottom: 10 }}>{record.lastError}</div>
          )}

          {/* ── QUOI / À QUI / QUAND — les trois questions de la maquette ───────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '74px 1fr', gap: '8px 12px', alignItems: 'start', padding: '12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
            <Rubrique>{t('QUOI')}</Rubrique>
            <span>
              {record.subject && (
                <span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-500)', marginBottom: 3 }}>
                  <Trans k='Objet : <1>{subject}</1>' vars={{ subject: record.subject }} parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </span>
              )}
              <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.55, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                {record.body.trim() ? `« ${record.body} »` : t('Message vide — il ne partira pas.')}
              </span>
            </span>

            <Rubrique>{t('À QUI')}</Rubrique>
            <span>
              <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
                {record.recipientCount ? t('{n} destinataire', { n: record.recipientCount }) : t('Audience non figée')}
                {/* ⚠️ LE PARTAGE SE DIT ICI AUSSI, et pas seulement en tuiles : « 1 240 destinataires »
                    seul laisserait croire que 1 240 messages partent. */}
                {temoins > 0 && (
                  <span style={{ fontWeight: 400, color: 'var(--ink-500)' }}>
                    {t('— dont {v} en groupe témoin, qui ne recevront rien', { v: nombre(temoins) })}</span>
                )}
              </span>
              <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2 }}>
                
                {/* ⚠️ UNE PHRASE ENTIÈRE PAR CANAL, PAS UNE PHRASE À TROU : « adresse renseignée »
                    est FAUX pour un porteur de pass, qui n'en a pas — et ce qui le rend joignable est
                    une carte qui existe vraiment chez le fournisseur. Une phrase à trou aurait
                    affiché une explication plausible et fausse (§23.0). */}
                {channelReachability(record.channel)}
                {temoins > 0 && <> {t('Le groupe témoin est tiré à la construction : c’est en le comparant aux servis qu’on saura ce que la campagne a causé.')}</>}
              </span>
            </span>

            <Rubrique>{t('QUAND')}</Rubrique>
            <span style={{ fontSize: '12.5px', color: 'var(--ink-700)' }}>
              {record.finishedAt ? t('Terminée le {v}', { v: dateCourte(record.finishedAt) })
                : record.startedAt ? t('Commencée le {v}', { v: dateCourte(record.startedAt) })
                  : t('Créée le {v} — départ au clic, la programmation viendra avec les déclencheurs.', { v: dateCourte(record.createdAt) })}
            </span>
          </div>

          {/* ── LE COMPTE RENDU, PAR DESTINATAIRE ──────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(112px,1fr))', gap: 8, marginTop: 10 }}>
            <Tuile label={t('Destinataires')} value={record.recipientCount} />
            <Tuile label={t('Partis')} value={tally.sent ?? 0} accent />
            <Tuile label={t('En échec')} value={tally.failed ?? 0} />
            {/* ⚠️ LES ÉCARTÉS SONT AFFICHÉS, TOUJOURS : ce sont des gens qui ont retiré leur
                consentement PENDANT l'envoi. Les cacher donnerait un compte rendu qui ne s'additionne pas. */}
            <Tuile label={t('Écartés')} value={tally.skipped ?? 0} />
            {/* ⚠️ SANS CETTE TUILE, LE COMPTE RENDU NE S'ADDITIONNE PLUS : 1 240 destinataires, 1 116
                partis, et personne pour dire où sont les 124 autres. Elle n'apparaît que s'il y en a —
                un zéro permanent sur toutes les campagnes sans témoin n'apprendrait rien. */}
            {temoins > 0 && <Tuile label={t('Témoins')} value={temoins} />}
            <Tuile label={t('Reste')} value={reste} />
          </div>

          {/* ⚠️ LE SOLDE AVANT LE BOUTON, pas après : c'est l'information qui décide du clic. */}
          <Solde credits={credits} verdict={verdict} destinataires={reste} suspendu={canalSuspendu} />

          {/* ⚠️ CE QUI VIENDRA EST DESSINÉ, PAS ABSENT (règle 2026-09-02) — trois gestes qu'on
              cherche naturellement sur une campagne. Les taire laisserait croire qu'on les a
              oubliés ; chacun porte donc son motif au survol. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--ink-100)' }}>
            <Button soon size="sm" variant="ghost"
              reason={t('Repartir d’une campagne passée sans la réécrire.')}>{t('Dupliquer')}</Button>
            <Button soon size="sm" variant="ghost"
              reason={t('La liste des destinataires, avec le sort de chacun — utile pour rattraper les échecs.')}>
              
              {t('Exporter le détail')}
            </Button>
            <Button soon size="sm" variant="ghost"
              reason={t('Suspendre en cours d’envoi demande un travail de fond : aujourd’hui l’envoi est synchrone, et il s’arrête entre deux lots.')}>
              
              {t('Suspendre')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** L'étiquette de rubrique de la maquette — capitales mono en teal, colonne de gauche. */
function Rubrique({ children }: { children: string }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', color: 'var(--teal-700)', fontWeight: 600, paddingTop: 2 }}>
      {children}
    </span>
  );
}

/** Une tuile de compte rendu — nombre au-dessus, ce qu'il compte en dessous. */
function Tuile({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div style={{ padding: '11px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', textAlign: 'center' }}>
      <div style={{ fontSize: '17px', fontWeight: 800, color: accent ? 'var(--teal-700)' : 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{nombre(value)}</div>
      <div style={{ fontSize: '10.5px', color: 'var(--ink-500)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// L'ÉCRAN
// ══════════════════════════════════════════════════════════════════════════════════════════════

export function Marketing() {
  const { view } = useStoreScope();
  const [rows, setRows] = useState<Campaign[]>([]);
  const [activity, setActivity] = useState<CampaignActivity | null>(null);
  /** Ce que les destinataires ont acheté, par campagne — servi dans le MÊME appel que les lignes. */
  const [attribution, setAttribution] = useState<CampaignAttribution[]>([]);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [canSend, setCanSend] = useState(false);
  /** Ce qui empêche TOUTE campagne ici — `null` = rien. Vient du SERVEUR, jamais deviné. */
  const [blocage, setBlocage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [redaction, setRedaction] = useState(false);
  const [q, setQ] = useState('');
  const [pastille, setPastille] = useState<Pastille>('all');
  /**
   * LES MODÈLES D’ENVOI AUTOMATIQUE.
   * ⚠️ `null` VEUT DIRE « PAS DISPONIBLE ICI », PAS « AUCUN » : le serveur refuse de monter ces routes
   * quand l’ordonnanceur n’a pas d’indice (404). Afficher « aucun modèle » dans ce cas laisserait
   * croire qu’on peut en créer un, et le prochain clic échouerait sans raison lisible.
   */
  const [modeles, setModeles] = useState<CampaignTemplate[] | null>(null);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA LISTE, et les modèles de même : ceux de l'ancien magasin, revenus après ceux
  //    du nouveau, s'afficheraient sous son nom — exactement ce que la dépendance au magasin ci-dessous veut empêcher.
  const latestList = useLatest();
  const latestTemplates = useLatest();
  const charger = useCallback(async () => {
    const isLatest = latestList();
    setChargement(true);
    try {
      const r = await api.campaigns();
      if (!isLatest()) return;
      setRows(r.rows); setActivity(r.activity); setAttribution(r.attribution); setCanSend(r.canSend);
      setBlocage(r.blocked ?? null); setErreur(null);
    } catch (e) {
      // ⚠️ LE MESSAGE RÉEL, et AUCUNE ligne : afficher les précédentes sous une erreur laisserait
      //    croire qu'elles sont à jour.
      if (!isLatest()) return;
      setRows([]); setActivity(null); setErreur(motif(e));
    } finally { if (isLatest()) setChargement(false); }
  }, [latestList]);

  /** Recharge les modèles — appelé à l’ouverture, et après chaque geste qui les change. */
  const chargerModeles = useCallback(async () => {
    const isLatest = latestTemplates();
    try {
      const r = await api.campaignTemplates();
      if (isLatest()) setModeles(r.rows);
    } catch {
      // ⚠️ ON NE DISTINGUE PAS ICI un 404 (routes non montées) d’une panne de lecture, et c’est
      //    assumé : dans les deux cas l’écran ne peut RIEN affirmer sur les envois automatiques, et
      //    la seule chose honnête est de ne pas prétendre qu’il n’y en a aucun.
      if (isLatest()) setModeles(null);
    }
  }, [latestTemplates]);

  // ⚠️ Le magasin de SESSION est dans les dépendances : changer de magasin doit recharger, sinon
  //    l'écran garderait les campagnes du précédent sous le nom du nouveau.
  const gln = view?.active?.gln;
  useEffect(() => { void charger(); }, [charger, gln]);
  useEffect(() => { void chargerModeles(); }, [chargerModeles, gln]);

  // ⚠️ LE SOLDE À L'OUVERTURE DE L'ONGLET — 1ᵉʳ des trois moments du cadrage §11.6. Un échec de
  //    lecture ne casse pas l'écran : la tuile dit alors qu'elle n'a pas de solde, avec le motif.
  useEffect(() => {
    let vivant = true;
    api.campaignCredits('sms')
      .then((r) => { if (vivant) setCredits(r.credits); })
      .catch(() => { if (vivant) setCredits(null); });
    return () => { vivant = false; };
  }, [gln]);

  /**
   * SUSPEND OU REMET EN SERVICE — et RECHARGE, parce que le serveur recalcule l’échéance.
   * ⚠️ ON NE DEVINE PAS LA NOUVELLE ÉCHÉANCE À L’ÉCRAN : reprendre la recalcule depuis MAINTENANT,
   * et un affichage optimiste montrerait l’ancienne date — celle qui vient précisément d’être jetée.
   */
  const basculer = async (m: CampaignTemplate) => {
    try {
      const { record } = await api.setCampaignTemplateActive(m.id, !m.isActive);
      showToast(record.isActive
        ? t('« {name} » repart — prochain envoi le {v}.', { name: record.name, v: echeanceLisible(record.nextDueAt) })
        : t('« {name} » est suspendu — plus rien ne part.', { name: record.name }), 'ok');
      await chargerModeles();
    } catch (e) { showToast(t('Geste refusé : {v}', { v: motif(e) }), 'err'); }
  };

  /**
   * RETIRE un modèle.
   * ⚠️ LES CAMPAGNES DÉJÀ ENGENDRÉES RESTENT, et la confirmation le dit : leur compte rendu et leur
   * mesure d’effet n’appartiennent pas au modèle. Sans cette phrase, un exploitant hésitera à
   * supprimer, de peur d’effacer son historique d’envois.
   */
  const retirer = async (m: CampaignTemplate) => {
    const ok = await confirm({
      title: t('Retirer « {name} » ?', { name: m.name }),
      message: t('Plus aucun envoi ne partira de ce modèle. Les campagnes qu’il a déjà engendrées restent, avec leur compte rendu et leur mesure d’effet.'),
      confirmLabel: t('Retirer'),
    });
    if (!ok) return;
    try {
      await api.deleteCampaignTemplate(m.id);
      showToast(t('« {name} » retiré.', { name: m.name }), 'ok');
      await chargerModeles();
    } catch (e) { showToast(t('Suppression refusée : {v}', { v: motif(e) }), 'err'); }
  };

  const brouillons = rows.filter((c) => c.status === 'draft').length;

  const visibles = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((c) => {
      if (pastille === 'draft' && c.status !== 'draft') return false;
      if (pastille === 'ponct' && c.status === 'draft') return false;
      // ⚠️ `auto` ne peut RIEN rendre tant qu'il n'y a pas de déclencheurs — la pastille est
      //    désactivée plus bas, ce filtre n'est là que pour rester juste le jour où elle s'activera.
      if (pastille === 'auto') return false;
      if (!query) return true;
      return c.name.toLowerCase().includes(query) || channelLabel(c.channel).toLowerCase().includes(query);
    });
  }, [rows, q, pastille]);

  const pastilles: { v: Pastille; l: string; n: number; soon?: string }[] = [
    { v: 'all', l: t('Toutes'), n: rows.length },
    {
      v: 'auto', l: t('Automatiques'), n: 0,
      soon: t('À venir — une campagne automatique se déclenche seule (anniversaire, inactivité), ce qui demande un ordonnanceur.'),
    },
    { v: 'ponct', l: t('Ponctuelles'), n: rows.length - brouillons },
    { v: 'draft', l: t('Brouillons'), n: brouillons },
  ];

  const compte = visibles.length === rows.length
    ? t('{n} campagne', { n: rows.length })
    : t('{v} sur {v2}', { v: nombre(visibles.length), v2: nombre(rows.length) });

  return (
    <>
      {/* ── L'en-tête — titre, sous-titre et « Nouvelle campagne » (maquette) ────────────────── */}
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Marketing')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
            <Trans k='Une campagne, trois questions : <1>quoi, à qui, quand.</1> En continu, c’est un automatisme — jamais de canal non consenti.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        {/* ⚠️ 🔴 LE BOUTON EST REFUSÉ, PAS CACHÉ. Le cacher laisserait chercher où est passée la
            fonction ; refusé avec son motif au survol, il dit ce qui manque et où le poser. */}
        <button type="button" onClick={() => { if (!blocage) setRedaction(true); }}
          disabled={!!blocage} title={blocage ?? undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' }}>
          <Trace d={IC.plus} />
          
          {t('Nouvelle campagne')}
        </button>
      </header>

      <Reperes activity={activity} attribution={attribution} credits={credits} />

      {/* ── LES ENVOIS AUTOMATIQUES ─────────────────────────────────────────────────────────── */}
      {/* ⚠️ 🔴 SANS CETTE LISTE, UN MODÈLE MIS EN SERVICE EST INTROUVABLE. On pouvait le créer depuis
          l’assistant et plus jamais le revoir : ni le suspendre, ni le corriger, ni même savoir qu’il
          existe — alors qu’il envoie. Une fonction sans écran est pire qu’un bouton mort. */}
      {modeles !== null && modeles.length > 0 && (
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, padding: '11px 14px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Envois automatiques')}</span>
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
              
              {t('Chaque tour engendre une campagne, avec sa propre cible et son propre compte rendu.')}
            </span>
          </div>
          {modeles.map((m) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderTop: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{m.name}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 2 }}>
                  {cadenceLisible(m.recurrence)}
                </span>
              </span>
              {/* ⚠️ L’ÉCHÉANCE EST LA SEULE INFORMATION QUI DIT SI ÇA MARCHE. Un modèle « en service »
                  sans prochaine date ne partira jamais — et c’est exactement ce qu’il faut voir. */}
              <span style={{ fontSize: 'var(--t-note)', color: m.nextDueAt === null ? 'var(--ink-400)' : 'var(--ink-700)' }}>
                {m.isActive ? echeanceLisible(m.nextDueAt) : t('suspendu')}
              </span>
              <button type="button" onClick={() => { void basculer(m); }}
                style={{ height: 30, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-note)', fontWeight: 600, cursor: 'pointer' }}>
                {m.isActive ? t('Suspendre') : t('Remettre en service')}
              </button>
              <button type="button" onClick={() => { void retirer(m); }}
                style={{ height: 30, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-600)', fontSize: 'var(--t-note)', fontWeight: 600, cursor: 'pointer' }}>
                
                {t('Retirer')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── CE QUI EMPÊCHE TOUTE CAMPAGNE ─────────────────────────────────────────────────── */}
      {/* ⚠️ 🔴 EN PREMIER, ET AVANT MÊME LES REPÈRES : c’est la seule information qui change ce que
          l’exploitant peut faire ici. La découvrir au clic sur « Nouvelle campagne », ou pire après
          avoir rédigé un message, ferait perdre le travail — le même raisonnement que pour le
          transport absent, dit un cran plus haut parce que le blocage est total. */}
      {blocage && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 13px', marginBottom: 12, borderRadius: 'var(--r-sm)', background: 'var(--wa-bg)', color: 'var(--wa-fg)' }}>
          <Trace d={IC.alerte} />
          <span style={{ fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
            <strong>{t('Aucune campagne ne peut partir de ce magasin.')}</strong> {blocage}
          </span>
        </div>
      )}

      {/* ⚠️ CE QUI MANQUE SE DIT UNE FOIS, EN HAUT DE LA CARTE — pas au moment du clic sur « Lancer ».
          Découvrir au dernier geste qu'aucun transport n'est câblé fait perdre le travail de ciblage. */}
      {!canSend && !chargement && !erreur && (
        <div style={{ marginBottom: 10, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
          
          {t('Aucun transport d’envoi n’est câblé sur ce back-office : les campagnes s’écrivent et se comptent, elles ne partent pas encore.')}
        </div>
      )}

      {/* ── La carte : barre d'outils, lignes, pied ──────────────────────────────────────────── */}
      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
          {/* ⚠️ CAPSULE `field-search` DU KIT — le même champ que le catalogue, le stock et le journal
              (règle utilisateur 2026-08-27), là où la maquette dessinait sa propre loupe encadrée.
              ⚠️ Le placeholder promet EXACTEMENT ce que le filtre tient : le nom et le canal. */}
          <div className="field-search" style={{ flex: '1 1 210px', maxWidth: 330 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
            </svg>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t('Nom de campagne, canal…')} aria-label={t('Rechercher une campagne')} />
          </div>

          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {pastilles.map((p) => {
              const on = pastille === p.v;
              // ⚠️ LA PASTILLE « AUTOMATIQUES » RESTE VISIBLE ET INACTIVE. La retirer laisserait croire
              //    que la notion n'existe pas ; la laisser cliquable rendrait une liste vide sans dire
              //    pourquoi. Elle porte donc son motif, comme un bouton « à venir ».
              const dispo = !p.soon;
              return (
                <button key={p.v} type="button" title={p.soon} aria-pressed={on} aria-disabled={!dispo || undefined}
                  onClick={() => { if (dispo) setPastille(p.v); }}
                  className={on ? 'chip active' : 'chip'}
                  /* ⚠️ CLASSE `.chip` DE LA CHARTE — une première version repeignait la puce à la
                     main (bordure teal-400, fond teal-100), ce qui rejouait `.chip.active` en moins
                     bien : la charte, elle, DÉRIVE sa bordure active de la couleur de marque, donc
                     une app qui surcharge l'accent reste cohérente. Ne reste en style que
                     l'inactivité, que la recette ne couvre pas. */
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                    ...(dispo ? {} : { color: 'var(--ink-400)', cursor: 'not-allowed' }) }}>
                  {p.l}
                  <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>{dispo ? nombre(p.n) : t('à venir')}</span>
                </button>
              );
            })}
          </span>

          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '12px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{compte}</span>
        </div>

        {/* ⚠️ TROIS ÉTATS DISTINCTS, JAMAIS CONFONDUS : la lecture en cours, la panne avec son message
            réel, et le vide. Un « aucune campagne » affiché sur une erreur ferait croire à une base
            vide alors que le serveur n'a pas répondu. */}
        {erreur && <div role="alert" style={{ ...blocErreur, margin: 16 }}>{t('Liste illisible : {erreur}', { erreur })}</div>}
        {chargement && !erreur && (
          <div style={{ padding: '52px 20px', textAlign: 'center', color: 'var(--ink-500)', fontSize: '13px' }}>{t('Lecture…')}</div>
        )}
        {!chargement && !erreur && visibles.map((c) => (
          <Ligne key={c.id} c={c} onOuvrir={() => setOuverte(c.id)} />
        ))}
        {/* ⚠️ DEUX VIDES, DEUX ICÔNES, DEUX GESTES. « Rien en base » et « rien qui corresponde au
            filtre » ne se réparent pas pareil : le premier appelle une création, le second un
            filtre à desserrer. Les confondre sous un seul message enverrait chercher des campagnes
            qui n'ont jamais existé. C'est l'`EmptyState` du kit — même vide partout. */}
        {!chargement && !erreur && visibles.length === 0 && (
          rows.length ? (
            <EmptyState icon="search" title={t('Aucune campagne ne correspond')}
              note={t('Changez de filtre, ou videz la recherche pour revoir toute la liste.')} />
          ) : (
            <EmptyState icon="mail" title={t('Aucune campagne')}
              note={<><Trans k='Écrivez un brouillon, comptez son audience, puis figez-la. Seules les fiches avec une adresse <1>et</1> le consentement du canal sont retenues.' parts={[(x) => <em>{x}</em>]} /></>}
              action={<Button variant="primary" onClick={() => setRedaction(true)}>{t('Nouvelle campagne')}</Button>} />
          )
        )}

        {/* ⚠️ LE PIED DE LA MAQUETTE PORTE LE CAPPING, QUI N'EXISTE PAS ENCORE — et c'est écrit. Une
            valeur en dur (« 3 messages / mois ») ferait croire à un plafond appliqué, donc à une
            protection qui n'existe pas : le pire des mensonges d'écran.
            ⚠️ LE TEXTE VIENT DU CATALOGUE COMMUN, et il n'y a pas de seconde copie ici : quand il
            était écrit aux deux endroits, les deux se CONTREDISAIENT — celui-ci disait « aucune
            limite », l'assistant affirmait « jamais sur-sollicité ». Deux écrans du même back-office,
            deux réponses opposées à la même question. */}
        <div style={{ padding: '10px 16px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-100)', fontSize: '11.5px', color: 'var(--ink-500)' }}>
          <Trans k='Capping global — <1>à venir</1> : {capping}' vars={{ capping: t(SANS_MOTEUR.capping) }} parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </div>
      </div>

      <WizardCampagne open={redaction} onClose={() => setRedaction(false)} onCree={() => void charger()} />
      {ouverte && <Detail id={ouverte} onChange={() => void charger()} onClose={() => setOuverte(null)} />}
    </>
  );
}
